// src/components/AuthModal.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaEnvelope, 
  FaLock, 
  FaUser, 
  FaGoogle, 
  FaEye, 
  FaEyeSlash,
  FaArrowLeft
} from 'react-icons/fa';
import { signUp, login, resetPassword, signInWithGoogle } from '../redux/slices/authSlice';
import { closeAuthModal, setAuthMode, openAuthModal } from '../redux/slices/uiSlice';
import toast from 'react-hot-toast';
import Logo from './Logo';

const AuthModal = ({ isOpenByDefault = false }) => {
  const dispatch = useDispatch();
  const { isAuthModalOpen, authMode } = useSelector((state) => state.ui);
  const { loading } = useSelector((state) => state.auth);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  // Auto-open modal if isOpenByDefault is true and modal is not already open
  useEffect(() => {
    if (isOpenByDefault && !isAuthModalOpen) {
      dispatch(openAuthModal());
    }
  }, [isOpenByDefault, isAuthModalOpen, dispatch]);

  // Close modal on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isAuthModalOpen) {
        dispatch(closeAuthModal());
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isAuthModalOpen, dispatch]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isAuthModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isAuthModalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isForgotPassword) {
      if (!resetEmail) {
        toast.error('Please enter your valid email');
        return;
      }
      await dispatch(resetPassword(resetEmail));
      setIsForgotPassword(false);
      return;
    }
    
    if (authMode === 'signup') {
      if (!displayName) {
        toast.error('Please enter your name');
        return;
      }
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      const success = await dispatch(signUp(email, password, displayName));
      if (success) {
        dispatch(closeAuthModal());
        resetForm();
      }
    } else {
      const success = await dispatch(login(email, password));
      if (success) {
        dispatch(closeAuthModal());
        resetForm();
      }
    }
  };

  const handleGoogleSignIn = async () => {
    // Prevent multiple clicks
    if (loading) return;
    
    try {
      const success = await dispatch(signInWithGoogle());
      if (success) {
        dispatch(closeAuthModal());
        resetForm();
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      toast.error('Google sign in failed. Please try again.');
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setResetEmail('');
    setIsForgotPassword(false);
  };

  const switchMode = () => {
    dispatch(setAuthMode(authMode === 'login' ? 'signup' : 'login'));
    resetForm();
  };

  if (!isAuthModalOpen) return null;

  return (
    <AnimatePresence>
      {isAuthModalOpen && (
        <div className="auth-modal-overlay" onClick={() => dispatch(closeAuthModal())}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="auth-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="auth-modal-scrollable">
              <div className="auth-modal-header">
                <div className="auth-logo-wrapper">
                  <div className="auth-logo-animated">
                    <Logo size="large" />
                  </div>
                </div>
                <h2>{isForgotPassword ? 'Reset Password' : (authMode === 'login' ? 'Welcome Back' : 'Create Account')}</h2>
                <p>{isForgotPassword ? 'Enter your email to reset password' : (authMode === 'login' ? 'Sign in to continue trading' : 'Join TradeHawk and start trading smarter')}</p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                {!isForgotPassword && authMode === 'signup' && (
                  <div className="form-group">
                    <FaUser className="input-icon" />
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={isForgotPassword ? resetEmail : email}
                    onChange={(e) => isForgotPassword ? setResetEmail(e.target.value) : setEmail(e.target.value)}
                    required
                  />
                </div>

                {!isForgotPassword && (
                  <div className="form-group">
                    <FaLock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                )}

                {!isForgotPassword && authMode === 'login' && (
                  <button
                    type="button"
                    className="forgot-password"
                    onClick={() => setIsForgotPassword(true)}
                  >
                    Forgot Password?
                  </button>
                )}

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? (
                    <div className="spinner-small"></div>
                  ) : (
                    isForgotPassword ? 'Send Reset Email' : (authMode === 'login' ? 'Sign In' : 'Create Account')
                  )}
                </button>
              </form>

              {/* Google Sign In Button */}
              {!isForgotPassword && (
                <>
                  <div className="auth-divider">
                    <span>or</span>
                  </div>

                  <button 
                    onClick={handleGoogleSignIn} 
                    className="google-signin-btn"
                    disabled={loading}
                  >
                    <FaGoogle />
                    Continue with Google
                  </button>
                </>
              )}

              {!isForgotPassword && (
                <div className="auth-footer">
                  <p>
                    {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                    <button onClick={switchMode} className="auth-switch-btn">
                      {authMode === 'login' ? 'Sign Up' : 'Sign In'}
                    </button>
                  </p>
                </div>
              )}

              {isForgotPassword && (
                <div className="auth-footer">
                  <button onClick={() => setIsForgotPassword(false)} className="back-to-login">
                    <FaArrowLeft /> Back to Login
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;